import {
  useQuery,
  useMutation,
  type UseQueryResult,
  type UseMutationResult,
} from "@tanstack/react-query";
import { resolveTenantId } from "@/lib/tenant";

// ---- Response types mirroring worker/types.ts ----

export interface TenantBranding {
  logo_url?: string;
  primary_color?: string;
  accent_color?: string;
  bg_color?: string;
}

export interface PublicStatusResponse {
  tenant: {
    id: string;
    company_name: string;
    page_title: string | null;
    page_description: string | null;
  };
  overall_status: string;
  components: StatusComponent[];
  active_incidents: StatusIncident[];
}

export interface StatusComponent {
  id: number;
  name: string;
  description: string | null;
  group: string | null;
  status: string;
  last_check_at: string | null;
  response_ms: number | null;
  uptime_7d: number | null;
  uptime_30d: number | null;
  uptime_90d: number | null;
}

export interface StatusIncident {
  id: number;
  title: string;
  status: string;
  impact: string;
  created_at: string;
  updates: IncidentUpdateEntry[];
}

export interface IncidentUpdateEntry {
  status: string;
  message: string;
  created_at: string;
}

export interface PublicTrustResponse {
  tenant: {
    id: string;
    company_name: string;
    page_title: string | null;
    page_description: string | null;
    branding: TenantBranding | null;
    show_status_page: boolean;
    show_compliance: boolean;
    show_documents: boolean;
    nda_required: boolean;
  };
  compliance: ComplianceEntry[];
}

export interface ComplianceEntry {
  framework_code: string;
  framework_name: string;
  status: string;
  valid_until: string | null;
  last_audit_date: string | null;
}

export interface DocumentEntry {
  id: number;
  document_type: string;
  document_name: string;
  description: string | null;
  file_url: string | null;
  requires_nda: number;
  is_public: number;
  sort_order: number;
  updated_at?: string;
}

export interface AccessRequestPayload {
  name: string;
  email: string;
  company: string;
  purpose: string;
}

export interface AccessRequestResponse {
  ok: boolean;
  message?: string;
}

export interface VerifyTokenResponse {
  ok: boolean;
  tenant: {
    company_name: string;
    nda_required: boolean;
    nda_template_text: string | null;
    nda_company_name: string | null;
  };
  requesterName: string;
}

export interface SignNdaPayload {
  verificationToken: string;
  signerName: string;
}

export interface SkipNdaPayload {
  verificationToken: string;
}

export interface NdaResponse {
  ok: boolean;
  accessToken: string;
}

export interface SessionCheckResponse {
  valid: boolean;
  name: string;
  email: string;
  expiresAt: string;
}

// ---- API fetch helper ----

class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

export { ApiError };

async function fetchApi<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
    ...options,
  });

  if (!response.ok) {
    const body = await response.text().catch(() => "");
    throw new ApiError(
      body || `Request failed with status ${response.status}`,
      response.status
    );
  }

  return response.json() as Promise<T>;
}

// ---- Hooks ----

export function useTrustConfig(): UseQueryResult<PublicTrustResponse> {
  const tenantId = resolveTenantId();

  return useQuery({
    queryKey: ["trust", tenantId],
    queryFn: () => fetchApi<PublicTrustResponse>(`/api/trust/${tenantId}`),
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status === 404) return false;
      return failureCount < 2;
    },
  });
}

export function useStatus(): UseQueryResult<PublicStatusResponse> {
  const tenantId = resolveTenantId();

  return useQuery({
    queryKey: ["status", tenantId],
    queryFn: () => fetchApi<PublicStatusResponse>(`/api/status/${tenantId}`),
    refetchInterval: 60 * 1000,
    staleTime: 30 * 1000,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status === 404) return false;
      return failureCount < 2;
    },
  });
}

export function useComponents(): UseQueryResult<StatusComponent[]> {
  const tenantId = resolveTenantId();

  return useQuery({
    queryKey: ["components", tenantId],
    queryFn: () =>
      fetchApi<StatusComponent[]>(`/api/status/${tenantId}/components`),
    refetchInterval: 60 * 1000,
    staleTime: 30 * 1000,
  });
}

export function useIncidents(): UseQueryResult<StatusIncident[]> {
  const tenantId = resolveTenantId();

  return useQuery({
    queryKey: ["incidents", tenantId],
    queryFn: () =>
      fetchApi<StatusIncident[]>(`/api/status/${tenantId}/incidents`),
    refetchInterval: 2 * 60 * 1000,
    staleTime: 60 * 1000,
  });
}

export function useDocuments(): UseQueryResult<DocumentEntry[]> {
  const tenantId = resolveTenantId();

  return useQuery({
    queryKey: ["documents", tenantId],
    queryFn: () =>
      fetchApi<DocumentEntry[]>(`/api/trust/${tenantId}/documents`),
    staleTime: 5 * 60 * 1000,
  });
}

export function useSubmitAccessRequest(): UseMutationResult<
  AccessRequestResponse,
  Error,
  AccessRequestPayload
> {
  const tenantId = resolveTenantId();

  return useMutation({
    mutationFn: (payload: AccessRequestPayload) =>
      fetchApi<AccessRequestResponse>(
        `/api/access/${tenantId}/request`,
        {
          method: "POST",
          body: JSON.stringify(payload),
          credentials: "include",
        }
      ),
  });
}

export function useVerifyToken(
  token: string
): UseQueryResult<VerifyTokenResponse> {
  const tenantId = resolveTenantId();

  return useQuery({
    queryKey: ["verify", tenantId, token],
    queryFn: () =>
      fetchApi<VerifyTokenResponse>(
        `/api/access/${tenantId}/verify/${token}`,
        { credentials: "include" }
      ),
    enabled: !!token,
    retry: false,
    staleTime: Infinity,
  });
}

export function useSignNda(): UseMutationResult<
  NdaResponse,
  Error,
  SignNdaPayload
> {
  const tenantId = resolveTenantId();

  return useMutation({
    mutationFn: (payload: SignNdaPayload) =>
      fetchApi<NdaResponse>(`/api/access/${tenantId}/sign-nda`, {
        method: "POST",
        body: JSON.stringify(payload),
        credentials: "include",
      }),
  });
}

export function useSkipNda(): UseMutationResult<
  NdaResponse,
  Error,
  SkipNdaPayload
> {
  const tenantId = resolveTenantId();

  return useMutation({
    mutationFn: (payload: SkipNdaPayload) =>
      fetchApi<NdaResponse>(`/api/access/${tenantId}/skip-nda`, {
        method: "POST",
        body: JSON.stringify(payload),
        credentials: "include",
      }),
  });
}

export function useCheckSession(): UseQueryResult<SessionCheckResponse> {
  const tenantId = resolveTenantId();
  const token = localStorage.getItem("bs_access_token");

  return useQuery({
    queryKey: ["session", tenantId, token],
    queryFn: () =>
      fetchApi<SessionCheckResponse>(`/api/access/${tenantId}/session`, {
        credentials: "include",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }),
    enabled: !!token,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
}
